import { BrightSpan } from '@components/BrightSpan/BrightSpan';
import { formatDurationToString, formatNumber } from '@lib/utils/units';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HeadphonesRoundedIcon from '@mui/icons-material/HeadphonesRounded';
import HighQualityRoundedIcon from '@mui/icons-material/HighQualityRounded';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import MovieIcon from '@mui/icons-material/Movie';
import MovieFilterOutlinedIcon from '@mui/icons-material/MovieFilterOutlined';
import MusicNoteRoundedIcon from '@mui/icons-material/MusicNoteRounded';
import PodcastsIcon from '@mui/icons-material/Podcasts';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import {
  COST_APPROXIMATE_IMAGE_FLOOR_IN_CREDITS,
  COST_APPROXIMATE_INGEST_PER_HOUR_IN_CREDITS,
  COST_MUSIC_PER_MINUTE_IN_CREDITS,
  COST_PODCAST_CLIP_MAX_IN_CREDITS,
  COST_PODCAST_CLIP_MIN_IN_CREDITS,
  COST_SEARCH_PER_SEARCH_IN_CREDITS,
  COST_SFX_PER_EFFECT_IN_CREDITS,
  COST_VIDEO_CINEMATIC_PER_CLIP_IN_CREDITS,
  COST_VIDEO_FAST_PER_CLIP_IN_CREDITS,
  COST_VOICEOVER_PER_MINUTE_IN_CREDITS,
  COST_WATERMARK_REMOVAL_PER_MINUTE_IN_CREDITS,
  MINUTES_PER_RENDER_ESTIMATE,
  SECONDS_IN_HOUR,
} from '@stores/Billing';
import React from 'react';

/**
 * A single credit-usage row: an icon plus its (rich) text content. The icon is
 * a component so each surface can apply its own className/size; the content is
 * shared verbatim. All amounts are approximate (see the pricing-page footnote).
 */
export interface PlanCreditItem {
  Icon: React.ElementType;
  content: React.ReactNode;
}

export interface PlanCreditGroup {
  label: string;
  items: PlanCreditItem[];
}

/**
 * Single source of truth for what a plan's monthly credits convert into.
 * Consumed by the plan cards (`PlanFeatures`) that the Pricing page and the
 * paywall share, so the two never drift.
 */
export function getPlanCreditGroups(credits: number): PlanCreditGroup[] {
  const floor = (cost: number) => formatNumber(Math.floor(credits / cost));
  const round = (cost: number) => formatNumber(Math.round(credits / cost));

  return [
    {
      label: 'Create with credits:',
      items: [
        {
          Icon: PodcastsIcon,
          content: (
            <>
              {floor(COST_PODCAST_CLIP_MAX_IN_CREDITS)}-{floor(COST_PODCAST_CLIP_MIN_IN_CREDITS)}{' '}
              <strong>podcast clips</strong> <BrightSpan>Featured</BrightSpan>
            </>
          ),
        },
        {
          Icon: ImageSearchIcon,
          content: (
            <>
              {floor(COST_APPROXIMATE_IMAGE_FLOOR_IN_CREDITS)} <strong>AI images</strong>
            </>
          ),
        },
        {
          Icon: MovieFilterOutlinedIcon,
          content: (
            <>
              {floor(COST_VIDEO_FAST_PER_CLIP_IN_CREDITS)} fast or{' '}
              {floor(COST_VIDEO_CINEMATIC_PER_CLIP_IN_CREDITS)} cinematic{' '}
              <strong>AI video clips</strong>
            </>
          ),
        },
        {
          Icon: MusicNoteRoundedIcon,
          content: (
            <>
              {formatDurationToString((credits / COST_MUSIC_PER_MINUTE_IN_CREDITS) * 60, {
                precision: 0,
                detail: 1,
              })}{' '}
              of <strong>music</strong>
            </>
          ),
        },
        {
          Icon: RecordVoiceOverIcon,
          content: (
            <>
              {Math.round(credits / COST_VOICEOVER_PER_MINUTE_IN_CREDITS / 60)} h of{' '}
              <strong>voiceover</strong>
            </>
          ),
        },
        {
          Icon: VolumeUpRoundedIcon,
          content: (
            <>
              {floor(COST_SFX_PER_EFFECT_IN_CREDITS)} <strong>sound effects</strong>
            </>
          ),
        },
      ],
    },
    {
      label: 'Plus, credits can be used for:',
      items: [
        {
          Icon: MovieIcon,
          content: (
            <>
              {formatDurationToString(
                (credits / COST_APPROXIMATE_INGEST_PER_HOUR_IN_CREDITS) * SECONDS_IN_HOUR,
                { precision: 0, detail: 1 },
              )}{' '}
              of media <strong>ingest</strong>
            </>
          ),
        },
        {
          Icon: AutoAwesomeIcon,
          content: (
            <>
              Unlimited <strong>agent chats</strong>, billed by effort
            </>
          ),
        },
        {
          Icon: SearchRoundedIcon,
          content: (
            <>
              {floor(COST_SEARCH_PER_SEARCH_IN_CREDITS)} <strong>library searches</strong>
            </>
          ),
        },
        {
          Icon: HighQualityRoundedIcon,
          content: (
            <>
              Unlimited <strong>watermarked renders</strong>
            </>
          ),
        },
        {
          Icon: HighQualityRoundedIcon,
          content: (
            <>
              {round(COST_WATERMARK_REMOVAL_PER_MINUTE_IN_CREDITS * 60)} h{' '}
              <strong>watermark-free</strong> (
              {round(COST_WATERMARK_REMOVAL_PER_MINUTE_IN_CREDITS * MINUTES_PER_RENDER_ESTIMATE)}{' '}
              renders)
            </>
          ),
        },
        {
          Icon: HeadphonesRoundedIcon,
          content: <>+ speech separation, AI graphics &amp; more</>,
        },
      ],
    },
  ];
}

/**
 * The "amounts are approximate" disclaimer body, shared by the pricing page and
 * the paywall. Caller supplies the surrounding note element + the leading
 * asterisk/icon.
 */
export const PlanApproxNote = () => (
  <>
    <strong>Amounts are approximate.</strong> Agent work (chats, edits, describe-clip, summaries, AI
    graphics, library-wide scans) is billed by effort: simple tasks cost a little, complex ones cost
    more. Credits are shared across everything above.
  </>
);
